/**
 * AssignRoleModal — used for both "Add Role" and "Edit Role" on the User Management Edit page.
 * Mirrors Angular UserManagementAddRoleComponent in dialog mode.
 *
 * Angular opens the same UserManagementAddRoleComponent in a dialog with `dialogTitle: 'Edit User Role'`
 * and passes `{ userDataId, roleData }`. It shows all role categories (Liberty, PMC, Charity, Renter)
 * with checkboxes for Liberty (multi) and radio buttons for others (single), plus dynamic
 * company/property/organization dropdowns based on role type (including Charity / ATLANTA).
 */
import { useState, useEffect, useCallback } from 'react'
import { Loader2, Info, HelpingHand, Shield, Building2, User } from 'lucide-react'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { useUserRole } from '@/hooks/useUserRole'
import { showValidationMessage, formatApiError } from '@/lib/utils/notifications'
import { cn } from '@/lib/utils/helpers'
import { LIBERTY_ROLES, CHARITY_ROLES, COMPANY_SPECIFIC_ROLES } from '@/constants/roles'
import {
  getRolesConfig,
  getCompanies,
  getProperties,
  getOrganizations,
  assignRole,
  updateRole,
  removeRole,
} from '../services/userManagementApi'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import modalStyles from '@/features/application/styles/modals.module.css'
import type {AssignRoleRequest, AssignRoleModalProps, AssignRoleProcessedRole } from '../types'
import { buildFilteredRoles } from '../utils/buildAssignRoleBuckets'
import { FIN_MGR_ROLES, COMPANY_LEVEL, PROPERTY_LEVEL } from '../constants'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function needsCompany(r: string) {
  return COMPANY_LEVEL.includes(r) || PROPERTY_LEVEL.includes(r)
}
function needsProperty(r: string) {
  return PROPERTY_LEVEL.includes(r) || r === 'ROLE_REGIONAL_MANAGER'
}
function isFinMgr(r: string): boolean {
  return (FIN_MGR_ROLES as readonly string[]).includes(r)
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AssignRoleModal({
  isOpen,
  onClose,
  userId,
  existingRole,
  existingRoleKeys = [],
  onSuccess,
}: AssignRoleModalProps) {
  const { isAdmin, roleName: myRoleName } = useUserRole()
  const isEditMode = !!existingRole

  // Role buckets
  const [buckets, setBuckets] = useState<ReturnType<typeof buildFilteredRoles>>({ liberty: [], pmc: [], charity: [], renter: [] })
  const [loadingRoles, setLoadingRoles] = useState(true)

  // Selection state
  const [selectedRole, setSelectedRole] = useState('')
  const [roleType, setRoleType] = useState('')
  const [libertyChecked, setLibertyChecked] = useState<string[]>([])

  // Dropdown state
  const [companyId, setCompanyId] = useState('')
  const [propertyId, setPropertyId] = useState<string | string[]>('')
  const [orgId, setOrgId] = useState('')
  const [finMgrChecked, setFinMgrChecked] = useState(false)

  // Options
  const [companyOptions, setCompanyOptions] = useState<SearchableSelectOption[]>([])
  const [propertyOptions, setPropertyOptions] = useState<SearchableSelectOption[]>([])
  const [orgOptions, setOrgOptions] = useState<SearchableSelectOption[]>([])
  const [companiesLoading, setCompaniesLoading] = useState(false)
  const [propertiesLoading, setPropertiesLoading] = useState(false)
  const [orgsLoading, setOrgsLoading] = useState(false)

  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  // ─── Load role config ──────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setLoadingRoles(true)
    getRolesConfig()
      .then((data) => {
        if (cancelled) return
        const b = buildFilteredRoles(data, myRoleName)
        setBuckets(b)
        // Pre-select existing role in edit mode
        if (existingRole) {
          const isLib = (LIBERTY_ROLES as readonly string[]).includes(existingRole.role_name)
          if (isLib) {
            setLibertyChecked([existingRole.role_name])
            setOrgId('1')
          } else {
            setSelectedRole(existingRole.role_name)
            // Determine roleType from buckets (charity roles are not in buckets — detect by key)
            const inPmc = b.pmc.find((r) => r.key === existingRole.role_name)
            const inCharity = b.charity.find((r) => r.key === existingRole.role_name)
            if (inPmc) setRoleType(inPmc.roleType)
            else if (inCharity) setRoleType(inCharity.roleType)
            else if ((CHARITY_ROLES as readonly string[]).includes(existingRole.role_name)) {
              setRoleType('ATLANTA')
            } else {
              setRoleType('')
            }
          }
          if (existingRole.company_id) setCompanyId(String(existingRole.company_id))
          if (existingRole.property_id) setPropertyId(String(existingRole.property_id))
          if (existingRole.org_id) setOrgId(String(existingRole.org_id))
          setFinMgrChecked(existingRole.fin_mgr_attribute === 'YES')
        }
      })
      .catch(() => { /* notification handled by service */ })
      .finally(() => { if (!cancelled) setLoadingRoles(false) })
    return () => { cancelled = true }
  }, [isOpen, myRoleName, existingRole])

  /**
   * SearchableSelect shows raw `value` when no matching option exists. Seed options from the role row
   * (`company_name` / `property_name` / `org_name` from GET user) so edit mode displays names immediately.
   */
  useEffect(() => {
    if (!isOpen || loadingRoles || !existingRole) return
    if ((LIBERTY_ROLES as readonly string[]).includes(existingRole.role_name)) return

    const cid = existingRole.company_id
    const cname = existingRole.company_name?.trim()
    if (cid != null && cid !== 0 && cname) {
      const v = String(cid)
      setCompanyOptions((prev) => (prev.some((o) => o.value === v) ? prev : [{ value: v, label: cname }, ...prev]))
    }

    if (needsProperty(existingRole.role_name)) {
      const pid = existingRole.property_id
      const pname = existingRole.property_name?.trim()
      if (pid != null && pid !== 0 && pname) {
        const v = String(pid)
        setPropertyOptions((prev) =>
          prev.some((o) => o.value === v) ? prev : [{ value: v, label: pname }, ...prev],
        )
      }
    }

    const rawOid = existingRole.org_id
    const oname = existingRole.org_name?.trim()
    if (rawOid != null && String(rawOid) !== '' && String(rawOid) !== '1' && oname) {
      const v = String(rawOid)
      setOrgOptions((prev) => (prev.some((o) => o.value === v) ? prev : [{ value: v, label: oname }, ...prev]))
    }
  }, [isOpen, loadingRoles, existingRole])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedRole('')
      setRoleType('')
      setLibertyChecked([])
      setCompanyId('')
      setPropertyId('')
      setOrgId('')
      setFinMgrChecked(false)
      setFormErrors({})
      setCompanyOptions([])
      setPropertyOptions([])
      setOrgOptions([])
    }
  }, [isOpen])

  // ─── Dropdown loaders ──────────────────────────────────────────────

  const loadCompanies = useCallback(async (q?: string) => {
    setCompaniesLoading(true)
    try {
      const list = await getCompanies(q)
      let opts = list.map((c) => ({ value: String(c.company_id), label: c.company_name }))
      const seedId =
        existingRole?.company_id != null && existingRole.company_id !== 0
          ? String(existingRole.company_id)
          : ''
      const seedLabel = existingRole?.company_name?.trim()
      if (seedId && seedLabel && !opts.some((o) => o.value === seedId)) {
        opts = [{ value: seedId, label: seedLabel }, ...opts]
      }
      setCompanyOptions(opts)
    } catch { setCompanyOptions([]) }
    finally { setCompaniesLoading(false) }
  }, [existingRole])

  const loadProperties = useCallback(async (q?: string, cid?: string) => {
    setPropertiesLoading(true)
    try {
      const effectiveCid = cid ?? companyId
      const list = await getProperties(q, effectiveCid)
      let opts = list.map((p) => ({ value: String(p.property_id), label: p.property_name }))
      const seedId =
        existingRole?.property_id != null && existingRole.property_id !== 0
          ? String(existingRole.property_id)
          : ''
      const seedLabel = existingRole?.property_name?.trim()
      const companyMatches =
        existingRole?.company_id != null &&
        String(existingRole.company_id) === String(effectiveCid ?? '')
      if (seedId && seedLabel && companyMatches && !opts.some((o) => o.value === seedId)) {
        opts = [{ value: seedId, label: seedLabel }, ...opts]
      }
      setPropertyOptions(opts)
    } catch { setPropertyOptions([]) }
    finally { setPropertiesLoading(false) }
  }, [companyId, existingRole])

  const loadOrgs = useCallback(async (q?: string) => {
    setOrgsLoading(true)
    try {
      const list = await getOrganizations(q)
      const filtered = list.filter((o) => String(o.org_id) !== '1')
      let opts = filtered.map((o) => ({ value: String(o.org_id), label: o.org_name }))
      const rawOid = existingRole?.org_id
      const seedId = rawOid != null && String(rawOid) !== '' ? String(rawOid) : ''
      const seedLabel = existingRole?.org_name?.trim()
      if (seedId && seedLabel && seedId !== '1' && !opts.some((o) => o.value === seedId)) {
        opts = [{ value: seedId, label: seedLabel }, ...opts]
      }
      setOrgOptions(opts)
    } catch { setOrgOptions([]) }
    finally { setOrgsLoading(false) }
  }, [existingRole])

  const handleCompanySearch = useCallback((q: string) => {
    if (q.length > 0 && q.length % 2 !== 0) return
    void loadCompanies(q)
  }, [loadCompanies])

  const handlePropertySearch = useCallback((q: string) => {
    if (q.length > 0 && q.length % 2 !== 0) return
    void loadProperties(q)
  }, [loadProperties])

  const handleOrgSearch = useCallback((q: string) => {
    if (q.length > 0 && q.length % 2 !== 0) return
    void loadOrgs(q)
  }, [loadOrgs])

  // ─── Role change handler ──────────────────────────────────────────

  const handleRoleChange = (roleKey: string, catKey: string, inputType: 'radio' | 'checkbox') => {
    setFormErrors({})
    if (inputType === 'checkbox') {
      // Liberty: toggle checkbox
      setLibertyChecked((prev) =>
        prev.includes(roleKey) ? prev.filter((k) => k !== roleKey) : [...prev, roleKey]
      )
      setOrgId('1')
      // Clear single-select state
      setSelectedRole('')
      setRoleType('LIBERTY')
      setCompanyId('')
      setPropertyId('')
      return
    }

    // Radio: single selection
    setSelectedRole(roleKey)
    setRoleType(catKey)
    setLibertyChecked([])
    setFinMgrChecked(false)

    switch (catKey) {
      case 'COMPANY':
      case 'PROPERTY':
        setOrgId('')
        void loadCompanies()
        break
      case 'ATLANTA':
        setCompanyId('')
        setPropertyId('')
        void loadOrgs()
        break
      default:
        setCompanyId('')
        setPropertyId('')
        setOrgId('')
    }
  }

  // ─── Validation ────────────────────────────────────────────────────

  const isFormValid = (): boolean => {
    // Angular UI doesn't allow "no role selected", but in edit mode
    // we treat unselecting the current role as a remove action.
    if (isEditMode && existingRole && !selectedRole && libertyChecked.length === 0) return true
    if (libertyChecked.length > 0) return true
    if (!selectedRole) return false
    if (needsCompany(selectedRole) && !companyId) return false
    const propOk = Array.isArray(propertyId)
      ? propertyId.length > 0
      : !!propertyId
    if (needsProperty(selectedRole) && !propOk) return false
    if (roleType === 'ATLANTA' && !orgId) return false
    return true
  }

  const validate = (): boolean => {
    const err: Record<string, string> = {}
    if (isEditMode && existingRole && !selectedRole && libertyChecked.length === 0) {
      setFormErrors({})
      return true
    }
    if (!selectedRole && libertyChecked.length === 0) err.role = 'Role selection is required.'
    if (selectedRole && needsCompany(selectedRole) && !companyId) err.company = 'Company is required.'
    if (selectedRole && needsProperty(selectedRole)) {
      const propOk = Array.isArray(propertyId) ? propertyId.length > 0 : !!propertyId
      if (!propOk) err.property = 'Property is required.'
    }
    if (roleType === 'ATLANTA' && !orgId) err.org = 'Organization is required.'
    setFormErrors(err)
    return Object.keys(err).length === 0
  }

  // ─── Submit ────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    try {
      // Edit mode: unselecting everything => remove current role
      if (isEditMode && existingRole && !selectedRole && libertyChecked.length === 0) {
        await removeRole(userId, existingRole.role_id)
        onSuccess?.()
        onClose()
        return
      }

      let payload: AssignRoleRequest

      if (libertyChecked.length > 0) {
        payload = { role_name: libertyChecked, org_id: '1' }
      } else {
        payload = { role_name: selectedRole }
        if (companyId) payload.company_id = Number(companyId)
        if (needsProperty(selectedRole)) {
          if (Array.isArray(propertyId) && propertyId.length === 1) {
            payload.property_id = Number(propertyId[0])
          } else if (Array.isArray(propertyId) && propertyId.length > 1) {
            payload.property_id = propertyId.map((id) => Number(id))
          } else if (propertyId) {
            payload.property_id = Number(propertyId)
          }
        }
        if (roleType === 'ATLANTA' && orgId) payload.org_id = orgId
        if (isFinMgr(selectedRole) && finMgrChecked) payload.fin_mgr_attribute = true
      }

      // Angular parity: role_removed is set only when the NEW role_name no longer includes
      // the original role (array membership for Liberty multi-select, equality otherwise).
      if (isEditMode && existingRole) {
        const newRoleName = payload.role_name
        const stillHasOldRole = Array.isArray(newRoleName)
          ? newRoleName.includes(existingRole.role_name)
          : newRoleName === existingRole.role_name
        if (!stillHasOldRole) {
          payload.role_removed = existingRole.role_name
        }
      }

      if (isEditMode && existingRole) {
        const msg = await updateRole(userId, existingRole.role_id, payload)
        if (typeof msg === 'string' && msg.trim().toLowerCase().includes('already')) {
          showValidationMessage(msg, 'Validation Message')
          return
        }
      } else {
        const msg = await assignRole(userId, payload)
        if (typeof msg === 'string' && msg.trim().toLowerCase().includes('already')) {
          showValidationMessage(msg, 'Validation Message')
          return
        }
      }
      onSuccess?.()
      onClose()
    } catch (err: unknown) {
      showValidationMessage(formatApiError(err), 'Validation Message')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => { if (!submitting) onClose() }

  if (!isOpen) return null

  // Whether the target user already has any liberty role
  const targetHasLiberty = existingRoleKeys.some((k) =>
    (LIBERTY_ROLES as readonly string[]).includes(k)
  )

  const showLiberty = isAdmin && buckets.liberty.length > 0
  const showRenter = buckets.renter.length > 0
  const showPmc = buckets.pmc.length > 0
  const showCharity = buckets.charity.length > 0

  const showCompany = needsCompany(selectedRole)
  const showProperty = needsProperty(selectedRole)
  const showOrg = roleType === 'ATLANTA'
  const showFinMgr = isFinMgr(selectedRole) && isFormValid()
  const isMultiProp = (COMPANY_SPECIFIC_ROLES as readonly string[]).includes(myRoleName)

  const content = (
    <OnScreenModal
      isOpen={isOpen}
      title={isEditMode ? 'Edit User Role' : 'Assign User Role'}
      headerClassName="!px-5 sm:!px-6"
      onClose={handleClose}
      maxWidth="max-w-lg"
      bodyScrollOnly
      headerVariant="centeredTitle"
      /* Above UserManagementEditEmbeddedModal (z-10002) when nested */
      overlayClassName="!z-[10051]"
      overlayDataTestId="um-assign-modal-overlay"
      panelDataTestId="um-assign-modal"
      closeButtonDataTestId="um-assign-modal-close-btn"
      titleDataTestId="um-assign-modal-title"
      footer={
        loadingRoles ? undefined : (
          <div className={modalStyles.modalFooterActionGroup} data-testid="um-assign-modal-footer">
            <button
              type="submit"
              form="um-assign-modal-form"
              disabled={submitting || !isFormValid()}
              className={modalStyles.modalActionPrimary}
              data-testid="um-assign-modal-submit-btn"
            >
              {submitting && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}
              Submit
            </button>
            <button
              type="button"
              onClick={handleClose}
              className={modalStyles.modalActionSecondary}
              data-testid="um-assign-modal-cancel-btn"
            >
              Cancel
            </button>
          </div>
        )
      }
    >
      {loadingRoles ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-[rgb(21_151_255)]" />
        </div>
      ) : (
        <form id="um-assign-modal-form" onSubmit={handleSubmit} noValidate className="space-y-4" data-testid="um-assign-modal-form">
                {formErrors.role && (
                  <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 border border-rose-200" data-testid="um-assign-modal-role-error">
                    {formErrors.role}
                  </p>
                )}

                {/* ── Role categories (order: Charities → Liberty → PMC → Renter) ─ */}
                {showCharity && (
                  <RoleSection
                    title="Charities"
                    icon={HelpingHand}
                    roles={buckets.charity}
                    inputType="radio"
                    selectedRole={selectedRole}
                    checkedRoles={libertyChecked}
                    userRoleKeys={existingRoleKeys}
                    editingRoleKey={existingRole?.role_name ?? null}
                    targetHasLiberty={targetHasLiberty}
                    onChange={handleRoleChange}
                  />
                )}
                {showLiberty && (
                  <RoleSection
                    title="Liberty"
                    icon={Shield}
                    roles={buckets.liberty}
                    inputType="checkbox"
                    selectedRole={selectedRole}
                    checkedRoles={libertyChecked}
                    userRoleKeys={existingRoleKeys}
                    editingRoleKey={existingRole?.role_name ?? null}
                    targetHasLiberty={targetHasLiberty}
                    onChange={handleRoleChange}
                  />
                )}
                {showPmc && (
                  <RoleSection
                    title="Property Management Companies"
                    icon={Building2}
                    roles={buckets.pmc}
                    inputType="radio"
                    selectedRole={selectedRole}
                    checkedRoles={libertyChecked}
                    userRoleKeys={existingRoleKeys}
                    editingRoleKey={existingRole?.role_name ?? null}
                    targetHasLiberty={targetHasLiberty}
                    onChange={handleRoleChange}
                  />
                )}
                {showRenter && (
                  <RoleSection
                    title="Renter"
                    icon={User}
                    roles={buckets.renter}
                    inputType="radio"
                    selectedRole={selectedRole}
                    checkedRoles={libertyChecked}
                    userRoleKeys={existingRoleKeys}
                    editingRoleKey={existingRole?.role_name ?? null}
                    targetHasLiberty={targetHasLiberty}
                    onChange={handleRoleChange}
                  />
                )}

                {/* ── Dropdowns ────────────────────────────────────── */}
                {showCompany && (
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-slate-500">Company</label>
                    <SearchableSelect
                      options={companyOptions}
                      value={companyId}
                      onChange={(val) => {
                        const cid = typeof val === 'string' ? val : val[0] ?? ''
                        setCompanyId(cid)
                        setPropertyId('')
                        void loadProperties(undefined, cid)
                        setFormErrors((p) => ({ ...p, company: '' }))
                      }}
                      placeholder="Select Company"
                      searchPlaceholder="Search companies…"
                      onOpen={() => void loadCompanies()}
                      onSearch={handleCompanySearch}
                      loading={companiesLoading}
                      error={formErrors.company}
                      className="w-full"
                      triggerDataTestId="um-assign-modal-company-select"
                    />
                    {formErrors.company && <p className="mt-1 text-xs text-rose-600" data-testid="um-assign-modal-company-error">{formErrors.company}</p>}
                  </div>
                )}

                {showProperty && (
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-slate-500">Property</label>
                    <SearchableSelect
                      options={propertyOptions}
                      value={propertyId}
                      onChange={(val) => {
                        setPropertyId(typeof val === 'string' ? val : val)
                        setFormErrors((p) => ({ ...p, property: '' }))
                      }}
                      placeholder="Select Property"
                      searchPlaceholder="Search properties…"
                      onOpen={() => void loadProperties()}
                      onSearch={handlePropertySearch}
                      loading={propertiesLoading}
                      error={formErrors.property}
                      className="w-full"
                      multiple={isMultiProp}
                      triggerDataTestId="um-assign-modal-property-select"
                    />
                    {formErrors.property && <p className="mt-1 text-xs text-rose-600" data-testid="um-assign-modal-property-error">{formErrors.property}</p>}
                  </div>
                )}

                {showOrg && (
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-slate-500">Organization</label>
                    <SearchableSelect
                      options={orgOptions}
                      value={orgId}
                      onChange={(val) => {
                        setOrgId(typeof val === 'string' ? val : val[0] ?? '')
                        setFormErrors((p) => ({ ...p, org: '' }))
                      }}
                      placeholder="Select Organization"
                      searchPlaceholder="Search organizations…"
                      onOpen={() => void loadOrgs()}
                      onSearch={handleOrgSearch}
                      loading={orgsLoading}
                      error={formErrors.org}
                      className="w-full"
                      triggerDataTestId="um-assign-modal-org-select"
                    />
                    {formErrors.org && <p className="mt-1 text-xs text-rose-600" data-testid="um-assign-modal-org-error">{formErrors.org}</p>}
                  </div>
                )}

                {/* Finance Manager */}
                {showFinMgr && (
                  <div className="flex items-center h-[38px] pb-1">
                    <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700 select-none">
                      <input
                        type="checkbox"
                        checked={finMgrChecked}
                        onChange={(e) => setFinMgrChecked(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)] cursor-pointer"
                        data-testid="um-assign-modal-finmgr-checkbox"
                      />
                      <span>Finance Manager</span>
                    </label>
                  </div>
                )}
        </form>
      )}
    </OnScreenModal>
  )

  return content
}

// ─── RoleSection sub-component ────────────────────────────────────────────────

function RoleSection({
  title,
  icon: Icon,
  roles,
  inputType,
  selectedRole,
  checkedRoles,
  userRoleKeys,
  editingRoleKey,
  targetHasLiberty,
  onChange,
}: {
  title: string
  icon?: React.ElementType
  roles: AssignRoleProcessedRole[]
  inputType: 'radio' | 'checkbox'
  selectedRole: string
  checkedRoles: string[]
  userRoleKeys: string[]
  editingRoleKey?: string | null
  targetHasLiberty: boolean
  onChange: (roleKey: string, catKey: string, type: 'radio' | 'checkbox') => void
}) {
  const isCheckbox = inputType === 'checkbox'

  return (
    <div className="rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/60 to-white px-3.5 py-3.5 sm:px-4 sm:py-4 shadow-sm ring-1 ring-slate-100/50">
      <div className="mb-3 flex items-center gap-2.5 border-b border-slate-200/80 pb-2.5">
        {Icon && (
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#dbeafe] border border-[#c7ddff] text-[#2563eb] shadow-sm">
            <Icon className="h-3.5 w-3.5" aria-hidden />
          </span>
        )}
        <h3 className="text-sm sm:text-base font-bold text-slate-800">
          {title}
        </h3>
      </div>
      <div className="divide-y divide-slate-100/80">
        {roles.map((role) => {
          if (role.key === 'ROLE_REFUND_APPROVAL' && !targetHasLiberty) return null
          // Angular: already-assigned liberty roles are not editable, except when editing that same role.
          const alreadyAssignedLiberty =
            isCheckbox && userRoleKeys.includes(role.key) && role.key !== (editingRoleKey ?? null)
          const checked = isCheckbox
            ? checkedRoles.includes(role.key) || alreadyAssignedLiberty
            : selectedRole === role.key

          return (
            <div
              key={role.key}
              className="py-0.5"
              data-testid={`um-assign-modal-role-row-${role.key}`}
            >
              <label
                className={cn(
                  'flex w-full min-w-0 items-center gap-2.5 rounded-md px-0.5 py-1.5 -mx-0.5',
                  !alreadyAssignedLiberty && 'cursor-pointer',
                )}
              >
                <input
                  type={isCheckbox ? 'checkbox' : 'radio'}
                  name={isCheckbox ? undefined : 'modal_role_name'}
                  checked={checked}
                  disabled={alreadyAssignedLiberty}
                  onChange={() => {
                    if (alreadyAssignedLiberty) return
                    onChange(role.key, role.roleType, inputType)
                  }}
                  className={cn(
                    'h-4 w-4 shrink-0 border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]',
                    !alreadyAssignedLiberty && 'cursor-pointer',
                  )}
                  data-testid={`um-assign-modal-role-input-${role.key}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="inline-flex min-w-0 max-w-full flex-wrap items-center gap-x-2 gap-y-1.5">
                    <span className="text-sm text-slate-800 leading-snug break-words [word-break:break-word]">
                      {role.name}
                    </span>
                    {role.desc ? (
                      <div className="relative inline-flex shrink-0 items-center group/info">
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/35"
                          aria-label={`What this role means: ${role.name}`}
                        >
                          <Info className="h-3.5 w-3.5" aria-hidden />
                        </button>
                        <div
                          className="invisible pointer-events-none absolute left-0 top-full z-50 mt-1 w-[min(15rem,calc(100vw-5rem))] max-w-[240px] sm:max-w-[260px]
                            rounded-lg border border-amber-300/90 bg-amber-50 p-3 text-xs font-normal text-amber-950 opacity-0 shadow-xl ring-1 ring-amber-200/60
                            transition-all duration-150
                            group-hover/info:visible group-hover/info:opacity-100"
                          dangerouslySetInnerHTML={{ __html: role.desc }}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </label>
            </div>
          )
        })}
      </div>
    </div>
  )
}
